import { useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/shared/cn';
import { gateSeatLabel, type AgentDecisionKind } from './AgentInvestigation.transform';
import { GATE_GHOST_BUTTON, GATE_PRIMARY_BUTTON } from './gate-ui';

/** The prefilled check is the record of what was checked, so it is never clipped. */
const REASON_MIN_ROWS = 3;

/** Shared K13 decision row. Switching to Modify/Reject starts a fresh required reason. */
export function ActionGateControls({ reason, onReason, onDecision, seat, canDecide, cursorLabel }: {
  reason: string;
  onReason: (value: string) => void;
  onDecision: (kind: AgentDecisionKind) => void;
  seat: string;
  canDecide: boolean;
  cursorLabel: string;
}) {
  const [kind, setKind] = useState<AgentDecisionKind>('approved');
  const [approvalReason] = useState(reason);
  const reasonRef = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const node = reasonRef.current;
    if (!node) return;
    const styles = window.getComputedStyle(node);
    const lineHeight = Number.parseFloat(styles.lineHeight) || 0;
    const padding = Number.parseFloat(styles.paddingTop) + Number.parseFloat(styles.paddingBottom);
    const border = Number.parseFloat(styles.borderTopWidth) + Number.parseFloat(styles.borderBottomWidth);
    const minHeight = REASON_MIN_ROWS * lineHeight + padding + border;
    node.style.height = 'auto';
    // `scrollHeight` is padding-box; `box-sizing: border-box` needs the borders added back.
    node.style.height = `${Math.max(minHeight, node.scrollHeight + border)}px`;
  }, [reason, canDecide]);

  if (!canDecide) return <p className="border-t border-[var(--hairline)] pt-2 text-xs text-muted" data-action-awaiting>{gateSeatLabel(seat)} · Awaiting approval</p>;
  const choose = (next: AgentDecisionKind) => {
    if (next === 'approved' || next === kind) onDecision(next);
    else { setKind(next); onReason(''); }
  };
  return (
    <div className="space-y-2 border-t border-[var(--hairline)] pt-2">
      <h3 className="sr-only">Human decision · {cursorLabel}</h3>
      <textarea ref={reasonRef} aria-label="Decision reason" rows={REASON_MIN_ROWS}
        className="block w-full resize-none overflow-hidden rounded-lg border border-[var(--hairline)] bg-transparent px-2.5 py-1.5 text-xs leading-snug"
        placeholder="Reason" value={reason} onChange={(event) => onReason(event.target.value)} />
      <div className="flex flex-wrap gap-1.5" data-agent-decision-controls>
        <button type="button" className={cn(GATE_PRIMARY_BUTTON, 'h-8 px-3')} disabled={kind !== 'approved' || reason.trim().length < 3} onClick={() => choose('approved')}>Approve · {gateSeatLabel(seat)}</button>
        <button type="button" className={cn(GATE_GHOST_BUTTON, 'h-8 px-2.5')} disabled={kind === 'modified' && reason.trim().length < 3} onClick={() => choose('modified')}>{kind === 'modified' ? 'Confirm modify' : 'Modify'}</button>
        <button type="button" className={cn(GATE_GHOST_BUTTON, 'h-8 px-2.5')} disabled={kind === 'rejected' && reason.trim().length < 3} onClick={() => choose('rejected')}>{kind === 'rejected' ? 'Confirm reject' : 'Reject'}</button>
        {kind !== 'approved' ? <button type="button" className={cn(GATE_GHOST_BUTTON, 'h-8 px-2.5')} onClick={() => { setKind('approved'); onReason(approvalReason); }}>Cancel</button> : null}
      </div>
    </div>
  );
}
